import { Link } from "react-router-dom";
import { Radio } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";

/**
 * Shared auth layout. Two-column on desktop (form left, brand right);
 * single column on mobile. Linear-style restraint, no card chrome,
 * just generous space and a hairline separator.
 */
export function AuthShell({
  eyebrow,
  title,
  subtitle,
  children,
  footer,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col pt-safe pb-safe">
      {/* Top bar */}
      <header className="px-5 sm:px-8 lg:px-12 h-14 flex items-center justify-between hairline">
        <Link to="/" className="flex items-center gap-2 font-display font-semibold tracking-tight text-[15px]">
          <Radio className="h-[18px] w-[18px] text-primary" />
          EchoCRM
        </Link>
        <ThemeToggle />
      </header>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2">
        {/* Form column */}
        <main className="flex items-start lg:items-center justify-center px-5 sm:px-8 lg:px-12 py-10 sm:py-16">
          <div className="w-full max-w-[400px]">
            {eyebrow && (
              <p className="text-[12px] font-mono-accent uppercase tracking-[0.18em] text-muted-foreground mb-4">
                {eyebrow}
              </p>
            )}
            <h1 className="font-display font-semibold text-[28px] sm:text-[36px] tracking-[-0.025em] leading-[1.1]">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-3 text-[15px] text-muted-foreground leading-[1.55]">{subtitle}</p>
            )}

            <div className="mt-8">{children}</div>

            {footer && <div className="mt-6 text-[13px] text-muted-foreground">{footer}</div>}
          </div>
        </main>

        {/* Brand column, desktop only */}
        <aside className="hidden lg:flex relative items-center justify-center theme-glow-tl border-l border-border overflow-hidden">
          <div className="absolute inset-0 geometric-grid opacity-40" />
          <div className="relative max-w-[420px] px-12">
            <p className="text-[12px] font-mono-accent uppercase tracking-[0.18em] text-muted-foreground mb-5">
              EchoCRM
            </p>
            <p className="font-display text-[28px] xl:text-[34px] leading-[1.2] tracking-[-0.02em]">
              The CRM for sales reps who talk more than they type.
            </p>
            <p className="mt-5 text-[14px] text-muted-foreground leading-[1.6]">
              Voice notes, Telegram messages, and call recaps become a working pipeline, instantly.
            </p>
          </div>
        </aside>
      </div>

      <footer className="hairline-t px-5 sm:px-8 lg:px-12 py-4 text-[12px] text-muted-foreground flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <span>© {new Date().getFullYear()} EchoCRM</span>
        <span>
          Support:{" "}
          <a href="mailto:support@echocrm.app" className="hover:text-foreground">support@echocrm.app</a>
        </span>
      </footer>
    </div>
  );
}